import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Selection } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { fetchIngredients } from '@/api'
import type { SearchIngredient } from '@/api/types'
import { Button } from '@/components/Button'
import { ModalContent, ModalFooter } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'
import { CompactTable } from '@/components/Table'
import LanguaLSelector from '../LanguaLSelector'

type Props = {
  selection: SearchIngredient[]
  onChangeSelection: (selection: SearchIngredient[]) => void
  close: () => void
}

export default function FCDBIngredientsContent({ selection, onChangeSelection, close }: Props) {
  const { t } = useTranslation()
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set())

  const [query, setQuery] = useState({
    includeLanguaLSubtree: true,
    languaLID: [],
    expand: true, // This is required for searching for languaLID
  })

  const { data = [], isLoading } = useQuery({
    queryKey: ['ingredientsSearch', query],
    queryFn: () => fetchIngredients(query),
  })

  const handleQueryChange = (attrName: string, value: string) => {
    setQuery({
      ...query,
      [attrName]: value,
    })
  }

  return (
    <>
      <ModalContent
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 4fr',
          maxHeight: '100%',
        }}>
        <LanguaLSelector
          enableSearch={true}
          includeSubtree={query.includeLanguaLSubtree}
          selectedDescriptorIDs={query.languaLID || []}
          onChangeSelection={(value) => {
            handleQueryChange('languaLID', value)
          }}
          onChangeIncludeTree={(value) => handleQueryChange('includeLanguaLSubtree', value)}
        />
        {isLoading ? (
          <div style={{ minHeight: 400 }}>
            <Spinner />
          </div>
        ) : (
          <CompactTable
            aria-label="FCDB ingredients"
            selectionMode="multiple"
            onSelectionChange={setSelectedKeys}
            rowId="idIngredient"
            sorting
            search={{
              columns: ['name', 'englishName'],
            }}
            columns={[
              {
                key: 'idIngredient',
                label: 'ID',
                width: 100,
              },
              {
                key: 'name',
                label: t('ingredient.name'),
              },
              {
                key: 'englishName',
                label: t('ingredient.englishName'),
              },
            ]}
            rows={data}
          />
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          variant="primary"
          onPress={() => {
            const ids = Array.from(selectedKeys).filter(
              (itemId) => !selection.some((sel) => sel.idIngredient === itemId),
            )

            const selIngredients = data?.filter((item) => ids.includes(item.idIngredient))

            onChangeSelection([...selection, ...selIngredients])

            close()
          }}>
          {t('saveSelection')}
        </Button>
      </ModalFooter>
    </>
  )
}
